import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { useAppDispatch, useAppSelector } from './store';
import { setIsLoading } from './store/slices/metadataSlice';
import config from './config/env';
import './styles/global.scss';

// Pages
import StartChallenge from './pages/StartChallenge';
import Loading from './pages/Loading';
import ThankYou from './pages/ThankYou';

// Components
import AuthGuard from './components/AuthGuard';
import { Toast } from './components/ui';
import ConfirmationModal from './components/ConfirmationModal';

// Placeholder pages (to be implemented)
const PaymentPage = React.lazy(() => import('./pages/Payment'));
const ChallengeStepsPage = React.lazy(() => import('./pages/ChallengeSteps'));
const SummaryPage = React.lazy(() => import('./pages/Summary'));

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isLoading = useAppSelector((state) => state.metadata.isLoading);

  // Initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setIsLoading(false));
    }, 1000);
    return () => clearTimeout(timer);
  }, [dispatch]);

  // Initialize GTM
  useEffect(() => {
    if (config.isProduction) {
      TagManager.initialize({ gtmId: config.gtmId });
    }
  }, [location]);

  // Warn on refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="App">
      <React.Suspense fallback={<Loading />}>
        <Routes>
          <Route
            path="/"
            element={
              <AuthGuard>
                <StartChallenge />
              </AuthGuard>
            }
          />
          <Route
            path="/payment"
            element={
              <AuthGuard>
                <PaymentPage />
              </AuthGuard>
            }
          />
          <Route
            path="/startChallenge"
            element={
              <AuthGuard>
                <ChallengeStepsPage />
              </AuthGuard>
            }
          />
          <Route
            path="/summary"
            element={
              <AuthGuard>
                <SummaryPage />
              </AuthGuard>
            }
          />
          <Route
            path="/thankYou"
            element={
              <AuthGuard>
                <ThankYou />
              </AuthGuard>
            }
          />
        </Routes>
      </React.Suspense>
      <Toast />
      <ConfirmationModal />
    </div>
  );
};

export default App;
